'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { directApiClient } from '@/lib/api/direct-client';
import { invalidateAfterInventoryUpdate } from '@/lib/query-keys';
import { updateInventoryItem } from '@/lib/services/inventory-items';
import type { InventoryItem } from '@/lib/services/inventory-items';
import type {
  SetOtherInstructionRequest,
  SetOtherInstructionResponse
} from '@/lib/services/redemption';

/**
 * Mutation hook for setting or updating an Other-asset instruction.
 * PUT /matters/:matterId/inventory-items/:itemId/other-instruction
 */
export function useSetOtherInstruction(matterId: number) {
  const queryClient = useQueryClient();

  return useMutation<
    SetOtherInstructionResponse,
    Error,
    { itemId: number; data: SetOtherInstructionRequest }
  >({
    mutationFn: async ({ itemId, data }) => {
      return directApiClient.put<SetOtherInstructionResponse>(
        `/matters/${matterId}/inventory-items/${itemId}/other-instruction`,
        data
      );
    },
    onSuccess: async () => {
      await invalidateAfterInventoryUpdate(queryClient, matterId);
    }
  });
}

/**
 * Mark an Other-asset (collect-in-trust path) as Collected.
 * Sends the realised cash amount + the collect-in-trust instruction (confirmed)
 * alongside the terminal status, so the item is a coherent
 * "collect in trust → Completed" record in the redemption phase. `status:
 * 'collected'` alone already lands it in redemption's "completed" bucket;
 * stamping the instruction matters when the amount is recorded from the
 * inventory drawer (LF-4005), where the item may never have had a redemption
 * instruction set. The redemption mark-collected flow already sets these, so it
 * is a no-op there. The backend's `PUT /inventory-items/:id` merges `itemInfo`
 * with existing data, so other fields (name, description, …) are preserved.
 */
export function useMarkOtherCollected(matterId: number) {
  const queryClient = useQueryClient();

  return useMutation<
    InventoryItem,
    Error,
    { itemId: number; realisedValue: number; receivedAt?: string | null }
  >({
    mutationFn: async ({ itemId, realisedValue, receivedAt }) =>
      updateInventoryItem(matterId, itemId, {
        status: 'collected',
        itemInfo: {
          realisedValue,
          instruction: 'collect-in-trust',
          instructionsConfirmed: true
        },
        // Supplied together with the amount, this is the moment the money
        // arrived — the backend records a dated receipt from it. Omitted when
        // the lawyer cleared the date; the collection still saves.
        ...(receivedAt ? { receivedAt } : {})
      }),
    onSuccess: async () => {
      await invalidateAfterInventoryUpdate(queryClient, matterId);
    }
  });
}

/**
 * Mark an Other-asset (transfer-to-beneficiary path) as Transferred.
 * In-specie outcome — no realisedValue is set, so the asset contributes 0 to
 * the `REALISED_CASH` lens used by availableToDistribute.
 */
export function useMarkOtherTransferred(matterId: number) {
  const queryClient = useQueryClient();

  return useMutation<InventoryItem, Error, { itemId: number }>({
    mutationFn: async ({ itemId }) =>
      updateInventoryItem(matterId, itemId, { status: 'collected' }),
    onSuccess: async () => {
      await invalidateAfterInventoryUpdate(queryClient, matterId);
    }
  });
}
